import { useState } from "react";
import type { Summary } from "./runbookTypes";
import { navigate, repoPath, useCurrentRepo } from "./router";
import { useApiResource } from "./useApiResource";

// RunbookIndex is the spec §6.4 runbook index: purpose, environment, risk
// level, blast radius, prerequisites (count), related ADRs. Like ADRIndex —
// and unlike the 425-row capability register — this stays a plain
// searchable table over the whole (8-row) GET /api/runbooks response.
export function RunbookIndex({ token }: { token: string }) {
  const repo = useCurrentRepo();
  const { status, data, message } = useApiResource<Summary[]>(token, repo, "/runbooks");
  const runbooks = data ?? [];
  const [query, setQuery] = useState("");

  if (status === "loading") {
    return (
      <section className="panel">
        <h2>Runbooks</h2>
        <p>Loading…</p>
      </section>
    );
  }
  if (status !== "ready") {
    return (
      <section className="panel">
        <h2>Runbooks</h2>
        <p className={status === "error" ? "err" : undefined}>{message}</p>
      </section>
    );
  }

  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? runbooks.filter((r) => `${r.title} ${r.environment} ${r.purpose}`.toLowerCase().includes(needle))
    : runbooks;

  return (
    <section className="panel register">
      <h2>Runbooks</h2>
      <div className="register-toolbar">
        <input
          type="search"
          role="searchbox"
          aria-label="Search runbooks"
          placeholder="Search title, environment, purpose…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <p className="muted-note">
        {filtered.length} of {runbooks.length} runbooks
      </p>
      <div className="table-scroll">
        <table className="register-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Environment</th>
              <th>Risk level</th>
              <th>Blast radius</th>
              <th>Related ADRs</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} tabIndex={0} onClick={() => navigate(repoPath(repo, "runbooks", r.id))}>
                <td>{r.title}</td>
                <td>
                  {r.environment || "—"}
                  {r.environment && r.environmentInferred ? " (inferred)" : ""}
                </td>
                <td className={r.riskLevel === "high" ? "risk-high" : undefined}>
                  {r.riskLevel || "—"}
                  {r.riskLevel && r.riskLevelInferred ? " (inferred)" : ""}
                </td>
                <td>{r.blastRadius || "—"}</td>
                <td>{(r.relatedAdrIds ?? []).join(", ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
